import React, {useMemo} from "react";

import {useMutation} from "@tanstack/react-query";
import {useSearchParams} from "react-router-dom";

import {ResponseError} from "@globals/types/utilityTypes";

import Footer from "@components/layout/Footer";

import RegistrationForm from "./RegistrationForm";
import {formatLocationOptions} from "./RegistrationForm/utils/formatLocationOptions";
import {
  InviteFormValues,
  MatFormValues,
} from "./RegistrationForm/utils/validations";
import {createUser} from "./RegistrationService";
import styles from "./index.scss";
import useCompanyLocations from "./useCompanyLocations";
import {formatFormError, formatLocationsError} from "./utils/formatError";
import {getOperatorLogo} from "./utils/getOperatorLogo";

const RegistrationApp = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const {
    isPending: isLoading,
    error,
    mutate: register,
  } = useMutation({mutationFn: createUser});

  const isInviteFlow = Boolean(
    searchParams.get("key") && searchParams.get("email")
  );
  const isWelcomeMatFlow = Boolean(
    searchParams.get("company") && searchParams.get("operator")
  );

  const {
    isLoading: isLocationsLoading,
    data: locationsResponse,
    error: locationsError,
    isSuccess: locationsSuccess,
  } = useCompanyLocations();

  const invitationError = useMemo(() => {
    if (!isInviteFlow && !isWelcomeMatFlow) {
      return "You need an invitation to join. Please check your email or contact your program administrator.";
    }
  }, [isInviteFlow, isWelcomeMatFlow]);

  const formattedError = useMemo(() => {
    if (locationsSuccess && !locationsResponse?.locations?.length) {
      return "Sorry, looks like this link is no longer valid!";
    }
    return (
      (error && formatFormError(error as ResponseError)) ||
      invitationError ||
      (locationsError &&
        formatLocationsError(locationsError as unknown as Error))
    );
  }, [error, invitationError, locationsError, locationsResponse]);

  const isSubmitDisabled = Boolean(invitationError);

  const onSubmit = (values: MatFormValues & InviteFormValues) => {
    let additionalFields = {};
    if (isInviteFlow) {
      additionalFields = {
        email: searchParams.get("email") as string,
        registration_key: searchParams.get("key") as string,
        delivery_location_id: null,
      };
    } else if (isWelcomeMatFlow) {
      additionalFields = {
        welcome_mat_slug: searchParams.get("company") as string,
        operator_slug: searchParams.get("operator") as string,
      };
    }

    register(
      {
        registration_key: null,
        ...values,
        ...additionalFields,
      },
      {
        onSuccess: () => {
          window.location.assign("/");
        },
      }
    );
  };

  const operatorLogo = getOperatorLogo(searchParams.get("operator"));

  return (
    <>
      <main className={styles.registrationContainer}>
        <div className={styles.registrationWrapper}>
          <div className={styles.heading}>
            <h1>WELCOME!</h1>
            <p>
              You're only a few steps away from joining the club. Please
              complete your registration.
            </p>
          </div>
          <div className={styles.formPanelContainer}>
            <div className={styles.logoContainer}>
              <img src={operatorLogo.src} alt={operatorLogo.alt} />
            </div>
            <h2 className={styles.formTitle}>SIGN UP</h2>
            {Boolean(formattedError) && (
              <div
                className={styles.errorMsg}
                dangerouslySetInnerHTML={{__html: formattedError as string}}
              ></div>
            )}
            <RegistrationForm
              onSubmit={onSubmit}
              locationOptions={
                locationsResponse?.locations &&
                formatLocationOptions(locationsResponse.locations)
              }
              isWelcomeMatFlow={isWelcomeMatFlow}
              isLoading={isLoading || isLocationsLoading}
              isSubmitDisabled={isSubmitDisabled}
            />
            <div>
              <p>
                <span>
                  By clicking "Sign up" above, you agree to EAT Club's
                </span>
                <a href="/terms">Terms and Conditions of Use</a>
                <span>and</span>
                <a href="/privacy">Privacy Policy.</a>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default RegistrationApp;
